<template>
  <div>
    <transition name="slide-fade">
      <div v-if="mqttStatus !== 'connected'" class="mqtt-banner" :class="mqttStatus">
        <span class="status-dot" :class="mqttStatus"></span>
        <v-icon small class="banner-icon" :class="{ 'spin': mqttStatus === 'reconnecting' }">
          {{ mqttStatus === 'reconnecting' ? 'sync' : mqttStatus === 'offline' ? 'wifi_off' : 'access_time' }}
        </v-icon>
        <span class="banner-text">
          {{ mqttStatus === 'reconnecting' ? 'Reconnecting to live feed…' : mqttStatus === 'offline' ? 'Live feed disconnected' : 'Data may be outdated' }}
        </span>
      </div>
    </transition>
    <div v-if="mqttStatus === 'connected'" class="live-indicator">
      <span class="live-dot"></span>
      <span class="live-text">Live</span>
    </div>

    <div class="legend-container">
      <MapLegend
        v-for="leg in legend"
        :key="leg.name"
        class="px-2 py-1"
        :text="$t(leg.name)"
        :shape="leg.attr"
      />
    </div>
    <div class="container-height">
      <div v-for="(block, index) in blockList" class="data-card ml-2 mr-2 mb-3" :key="block.text">
        <p class="block-name text-xs-center pt-1">{{ block.text }}</p>
        <v-divider></v-divider>
        <div>
          <div v-for="(data1, deviceId) in groupedByBlock[block.id]" :key="deviceId">
            <v-divider></v-divider>
            <div class="device-header">
              <p class="text-xs-center pt-1">{{ data1.name }}</p>
              <span v-if="deviceConnected[deviceId] !== false" class="device-badge live">Live</span>
              <span v-else class="device-badge outdated">Data Outdated</span>
            </div>
            <v-layout class="pl-2 pr-2 pb-2" row wrap>
              <v-flex v-for="(value, param) in data1" :key="param" xs2>
                <div v-if="param != 'name' && paramConfig[param] != undefined && paramConfig[param]['signal_type'] == 'status'" class="status-card" :class="deviceConnected[deviceId] === false ? 'no-status' : value == paramConfig[param]['healthy_value'] ? 'status-on' : value == null ? 'no-status' : 'status-off'">
                  <div class="truncate pl-1 pr-1">{{ nameMap[deviceId][param] }}</div>
                </div>
                <div v-else-if="param != 'name'" class="status-card">
                  <div class="truncate pl-1 pr-1">{{ nameMap[deviceId][param] }} :</div>
                  <div v-if="value != null" class="value-color">{{ value }}</div>
                  <div v-else class="no-data">No Data</div>
                </div>
              </v-flex>
            </v-layout>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import relayApi from '../services/relay'
import { mapGettersMixin } from '@/util/mixin/index'
import { mqttStatusMixin } from '@/util/mixin/mqttStatusMixin'
import MapLegend from '../components/common/MapLegend.vue'

export default {
  mixins: [ mapGettersMixin, mqttStatusMixin ],
  components: {
    MapLegend
  },
  data() {
    return {
      blockList: [],
      relayData: {},
      paramConfig: {},
      nameMap: {},
      deviceConnected: {},
      apiDataLoaded: false,
    }
  },
  created() {
    this.initialStates()
  },
  computed: {
    ...mapGetters(['getSelectedPlantId', 'getGeneralState', 'getRules']),
    relayType() {
      const compConfig = this.getRules(['rule', 'comp_config']);
      const relayType = (compConfig && compConfig.relay && compConfig.relay.options && compConfig.relay.options.relay_type) ? compConfig.relay.options.relay_type : null;
      return (relayType && !isNaN(Number(relayType))) ? relayType : null;
    },
    groupedByBlock() {
      const groupedObject = {}
      Object.keys(this.relayData).forEach(key => {
        const block = key.split('-')[0]
        if (!groupedObject[block]) {
          groupedObject[block] = {};
        }
        groupedObject[block][key] = this.relayData[key]
      })

      return groupedObject
    }
  },
  methods: {
    ...mapMutations([ 'SET_PROGRESS_BAR' ]),
    initialStates() {
      this.unsubscribeMqtt()
      this.resetMqttState()
      this.blockList = []
      this.relayData = {}
      this.paramConfig = {}
      this.nameMap = {}
      this.deviceConnected = {}
      this.callAllApi()
    },
    async callAllApi() {
      await this.getBlockList()
      await this.getRelayData()
      this.apiDataLoaded = true
      this.subscribeMqtt()
    },
    async getRelayData() {
      try {
        this.SET_PROGRESS_BAR(true)
        const response = await relayApi.getRelayData(this.getSelectedPlantId, this.relayType)
        this.relayData = response.data.data || {}
        this.paramConfig = response.data.config || {}
        this.nameMap = response.data.name_map || {}
        Object.keys(this.relayData).forEach(deviceId => {
          this.$set(this.deviceConnected, deviceId, true)
          this._scheduleDeviceStaleTimer(deviceId)
        })
      } catch (error) {
        console.error('[Relay] Failed to fetch relay data:', error)
        this.SET_PROGRESS_BAR(false)
      } finally {
        if(Object.keys(this.groupedByBlock).length > 0) {
          this.SET_PROGRESS_BAR(false)
        }
      }
    },
    async getBlockList() {
      try {
        this.SET_PROGRESS_BAR(true)
        const response = await relayApi.getBlockList(this.getSelectedPlantId, this.relayType)
        this.blockList = response.data.message
      } catch(err) {
        console.error('[Relay] Failed to fetch block list:', err)
        this.SET_PROGRESS_BAR(false)
      } finally {
        this.SET_PROGRESS_BAR(false)
      }
    },
    subscribeMqtt() {
      const plantId = this.getSelectedPlantId
      if (!plantId) return
      try {
        this.$mqtt.subscribe(`live/${plantId}/relay`)
        this.bindMqttEvents()
        this.startMqttStaleTimer()
      } catch (e) {
        console.error('[Relay] MQTT subscribe error:', e)
      }
    },
    unsubscribeMqtt(plantId = this.getSelectedPlantId) {
      if (!plantId) return
      try {
        this.$mqtt.unsubscribe(`live/${plantId}/relay`)
      } catch (e) {
        console.error('[Relay] MQTT unsubscribe error:', e)
      }
    },
    _scheduleDeviceStaleTimer(deviceId) {
      this._scheduleDeviceStaleTimerMixin(deviceId, (expiredDeviceId) => {
        if (this.nameMap[expiredDeviceId]) {
          Object.keys(this.nameMap[expiredDeviceId]).forEach(key => {
            if (key in this.relayData[expiredDeviceId]) {
              this.$set(this.relayData[expiredDeviceId], key, null)
            }
          })
        }
        this.$set(this.deviceConnected, expiredDeviceId, false)
      })
    },
    _markDeviceLive(deviceId) {
      this._markDeviceLiveMixin(deviceId, (expiredDeviceId) => {
        if (this.nameMap[expiredDeviceId]) {
          Object.keys(this.nameMap[expiredDeviceId]).forEach(key => {
            if (key in this.relayData[expiredDeviceId]) {
              this.$set(this.relayData[expiredDeviceId], key, null)
            }
          })
        }
        this.$set(this.deviceConnected, expiredDeviceId, false)
      })
    },
    bindMqttEvents() {
      if (this.mqttBound) return
      this.mqttMessageHandler = (topic, message) => {
        if (!this.apiDataLoaded) return
        try {
          const payload = JSON.parse(message.toString())
          if (topic.startsWith('live/') && topic.endsWith('/relay')) {
            const deviceList = payload.deviceList
            if (!Array.isArray(deviceList) || !deviceList.length) return

            deviceList.forEach(device => {
              if (device.ignore_status === 1) return

              const deviceId = `${device.block_id}-${device.relay_id}`
              if (!this.relayData[deviceId]) return

              if (this.nameMap[deviceId]) {
                Object.keys(this.nameMap[deviceId]).forEach(key => {
                  if (!(key in device) && key in this.relayData[deviceId]) {
                    this.$set(this.relayData[deviceId], key, null)
                  }
                })
              }

              for (const [key, value] of Object.entries(device)) {
                if (key !== 'name' && this.paramConfig[key]) {
                  this.$set(this.relayData[deviceId], key, value)
                }
              }

              this._markDeviceLive(deviceId)
            })

            this.mqttStatus = 'connected'
            this.startMqttStaleTimer()
          }
        } catch (e) {
          console.error('[Relay] MQTT parse error:', e)
        }
      }
      this.registerMqttHandlers(this.mqttMessageHandler)
    },
  },
  watch: {
    getSelectedPlantId(newVal, oldVal) {
      this.unsubscribeMqtt(oldVal)
      this.initialStates()
    }
  }
}
</script>

<style scoped>
.legend-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.data-card {
  background-color: var(--v-primary-base);
  border-radius: 12px;
  box-shadow: 0px 5px 5px 0px #334079;
}

.block-name {
  font-weight: bolder;
  font-size: 18px;
}

.device-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 8px;
}

.device-header p {
  margin: 0;
  flex: 1;
  text-align: center;
}

.device-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  animation: pulse-badge 2s ease-in-out infinite;
}

.device-badge.live {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  border: 1px solid rgba(76, 175, 80, 0.4);
}

.device-badge.outdated {
  background: rgba(255, 183, 77, 0.2);
  color: #FFB74D;
  border: 1px solid rgba(255, 183, 77, 0.4);
}

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.status-card {
  border-radius: 10px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--v-primary-darken1);
}

.status-on {
  border: 3px solid #3CB371;
}

.status-off {
  border: 3px solid #FF5252;
}

.no-status {
  border: 3px solid #9E9E9E;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-size: 14px;
}

.container-height {
  height: 70vh;
  overflow-y: auto;
}

.no-data {
  color: #ff5252;
  text-align: center;
}

.value-color {
  color: var(--v-number-base);
}

/* ── MQTT Banner ──────────────────────────────────── */
.mqtt-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 10px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.mqtt-banner.offline {
  background: linear-gradient(135deg, rgba(239, 83, 80, 0.15), rgba(239, 83, 80, 0.08));
  color: #EF5350;
  border: 1px solid rgba(239, 83, 80, 0.3);
}

.mqtt-banner.stale {
  background: linear-gradient(135deg, rgba(255, 183, 77, 0.15), rgba(255, 183, 77, 0.08));
  color: #FFB74D;
  border: 1px solid rgba(255, 183, 77, 0.3);
}

.mqtt-banner.reconnecting {
  background: linear-gradient(135deg, rgba(66, 165, 245, 0.15), rgba(66, 165, 245, 0.08));
  color: #42A5F5;
  border: 1px solid rgba(66, 165, 245, 0.3);
}

.banner-icon {
  font-size: 16px !important;
  opacity: 0.9;
}

.banner-text {
  flex: 1;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.offline {
  background: #EF5350;
  box-shadow: 0 0 6px rgba(239, 83, 80, 0.6);
}

.status-dot.stale {
  background: #FFB74D;
  box-shadow: 0 0 6px rgba(255, 183, 77, 0.6);
  animation: pulse-stale 2s ease-in-out infinite;
}

.status-dot.reconnecting {
  background: #42A5F5;
  box-shadow: 0 0 6px rgba(66, 165, 245, 0.6);
  animation: pulse-reconnect 1s ease-in-out infinite;
}

/* ── Live indicator (when connected) ─────────────── */
.live-indicator {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 4px 12px;
  margin-bottom: 6px;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4CAF50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
  animation: pulse-live 2s ease-in-out infinite;
}

.live-text {
  font-size: 11px;
  font-weight: 600;
  color: #4CAF50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ── Animations ──────────────────────────────────── */
@keyframes pulse-live {
  0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(76, 175, 80, 0.6); }
  50% { opacity: 0.6; box-shadow: 0 0 4px rgba(76, 175, 80, 0.3); }
}

@keyframes pulse-stale {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

@keyframes pulse-reconnect {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.7; }
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ── Transitions ─────────────────────────────────── */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}
.slide-fade-enter {
  transform: translateY(-10px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}
</style>
